const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class ApiService {
  constructor() {
    this.token = localStorage.getItem('access_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('access_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('access_token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 204) {
        return null;
      }

      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.detail || 'API Error');
        error.status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  // Auth
  async login(username, password) {
    return this.request('/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ username, password }),
    });
  }

  // Users
  async getMe() {
    return this.request('/admin-api/users/me');
  }

  async getUsers(page = 1, pageSize = 20, q = '', isActive = null) {
    const params = new URLSearchParams({
      page,
      page_size: pageSize,
    });
    if (q) params.append('q', q);
    if (isActive !== null) params.append('is_active', isActive);

    return this.request(`/admin-api/users?${params}`);
  }

  async getUser(userId) {
    return this.request(`/admin-api/users/${userId}`);
  }

  async createUser(data) {
    return this.request('/admin-api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(userId, data) {
    return this.request(`/admin-api/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(userId) {
    return this.request(`/admin-api/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Organizations
  async getOrganizations() {
    return this.request('/admin-api/organizations');
  }

  async getOrganization(orgId) {
    return this.request(`/admin-api/organizations/${orgId}`);
  }

  async createOrganization(data) {
    return this.request('/admin-api/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOrganization(orgId, data) {
    return this.request(`/admin-api/organizations/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(orgId) {
    return this.request(`/admin-api/organizations/${orgId}`, {
      method: 'DELETE',
    });
  }

  // Videos
  async getVideos(page = 1, pageSize = 20, userId = null, q = '', status = null) {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (userId) params.append('user_id', userId);
    if (q) params.append('q', q);
    if (status) params.append('status', status);

    return this.request(`/admin-api/videos?${params}`);
  }

  async getVideo(videoId) {
    return this.request(`/admin-api/videos/${videoId}`);
  }

  async updateVideo(videoId, data) {
    return this.request(`/admin-api/videos/${videoId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteVideo(videoId) {
    return this.request(`/admin-api/videos/${videoId}`, {
      method: 'DELETE',
    });
  }

  // Video Sessions
  async getVideoSessions(page = 1, pageSize = 20, userId = null, q = '', status = null) {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (userId) params.append('user_id', userId);
    if (q) params.append('q', q);
    if (status) params.append('status', status);

    return this.request(`/admin-api/video-sessions?${params}`);
  }

  async getVideoSession(sessionId) {
    return this.request(`/admin-api/video-sessions/${sessionId}`);
  }

  async updateVideoSession(sessionId, data) {
    return this.request(`/admin-api/video-sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteVideoSession(sessionId) {
    return this.request(`/admin-api/video-sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  // VR Glasses
  async getVRGlasses(page = 1, pageSize = 20, userId = null, q = '') {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (userId) params.append('user_id', userId);
    if (q) params.append('q', q);

    return this.request(`/admin-api/vr-glasses?${params}`);
  }

  async getVRGlass(recordId) {
    return this.request(`/admin-api/vr-glasses/${recordId}`);
  }

  async updateVRGlass(recordId, data) {
    return this.request(`/admin-api/vr-glasses/${recordId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteVRGlass(recordId) {
    return this.request(`/admin-api/vr-glasses/${recordId}`, {
      method: 'DELETE',
    });
  }

  // Questionnaires
  async getQuestionnaires() {
    return this.request('/admin-api/questionnaires');
  }

  async getQuestionnaireSubmissions(page = 1, pageSize = 20, userId = null, questionnaireId = null, q = '') {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (userId) params.append('user_id', userId);
    if (questionnaireId) params.append('questionnaire_id', questionnaireId);
    if (q) params.append('q', q);

    return this.request(`/admin-api/questionnaire-submissions?${params}`);
  }

  async getQuestionnaireSubmission(submissionId) {
    return this.request(`/admin-api/questionnaire-submissions/${submissionId}`);
  }

  async updateQuestionnaireSubmission(submissionId, data) {
    return this.request(`/admin-api/questionnaire-submissions/${submissionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteQuestionnaireSubmission(submissionId) {
    return this.request(`/admin-api/questionnaire-submissions/${submissionId}`, {
      method: 'DELETE',
    });
  }

  // Assessments
  async getAssessments() {
    return this.request('/admin-api/assessments');
  }

  async getAssessmentForm(assessmentId) {
    return this.request(`/admin-api/assessments/${assessmentId}/form`);
  }

  async getAssessmentSubmissions(page = 1, pageSize = 20, subjectUserId = null, assessmentId = null, q = '') {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (subjectUserId) params.append('subject_user_id', subjectUserId);
    if (assessmentId) params.append('assessment_id', assessmentId);
    if (q) params.append('q', q);

    return this.request(`/admin-api/assessment-submissions?${params}`);
  }

  async getAssessmentSubmission(submissionId) {
    return this.request(`/admin-api/assessment-submissions/${submissionId}`);
  }

  async createAssessmentSubmission(data) {
    return this.request('/admin-api/assessment-submissions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAssessmentSubmission(submissionId, data) {
    return this.request(`/admin-api/assessment-submissions/${submissionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAssessmentSubmission(submissionId) {
    return this.request(`/admin-api/assessment-submissions/${submissionId}`, {
      method: 'DELETE',
    });
  }

  async putAssessmentAnswer(submissionId, data) {
    return this.request(`/admin-api/assessment-submissions/${submissionId}/answers`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // IBS Forms
  async getIbsFormSubmissions(page = 1, pageSize = 20, userId = null, formId = null, q = '') {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (userId) params.append('user_id', userId);
    if (formId) params.append('form_id', formId);
    if (q) params.append('q', q);

    return this.request(`/admin-api/ibs-forms/submissions?${params}`);
  }

  async getIbsFormSubmission(submissionId) {
    return this.request(`/admin-api/ibs-forms/submissions/${submissionId}`);
  }
}

export default new ApiService();
